/**
 * 도움말 메뉴 — 짧은 안내만 두고, 자세한 것은 AI 어시스턴트에 넘긴다.
 *
 * 왜 이렇게 나누나: 사용 설명서는 쓰는 순간부터 낡고, 읽히지도 않는다. 「사용 안내」는 "무엇을 어디서 하나" 를
 * 주제별 한 줄 요약 + 요점 몇 줄로만 보이고, 각 주제에 「AI 어시스턴트에 물어보기」를 단다. 어시스턴트는 같은
 * 안내(help/usageGuide)를 근거로 답하고, 샘플이 있는 주제는 샘플 워크플로를 열어 그 위에서 설명한다.
 *
 * 메뉴: 사용 안내 · AI 어시스턴트에 물어보기 · 샘플 프로젝트 최신으로 바꾸기 · 정보. 스탠드얼론(로컬) 모드는 AI 탭이 없으므로 AI 항목을 감춘다.
 * (datamodeler MenuHelp.js 와 같은 구성)
 */
import React, { useEffect, useRef, useState } from 'react';
import { FiHelpCircle, FiBookOpen, FiInfo, FiMessageSquare, FiX, FiFileText, FiRefreshCw } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import { loadProjects, setSelectedProject } from '../../store/project/projectSlice';
import { refreshSample, usesStoreApi } from '../../services/repo';
import { showAlert, showConfirm } from '../common/AppDialog';
import { formatDateTime } from '../../utils/formatters';
import LogViewerDialog from '../eventlog/LogViewerDialog';
import { logUsage } from '../../services/eventLog';
import { usageTopics } from '../../help/usageGuide';
import { helpContent } from '../../help/content';
import { askAssistant } from '../../help/helpBus';
import { useAssistantStatus } from '../../hooks/useAssistantEnabled';
import { IS_LOCAL } from '../../config/mode';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';
import { BUILD_RELEASE, IS_DEV_SERVER, buildTimeText } from '../../config/buildInfo';

const Modal: React.FC<{ open: boolean; onClose: () => void; width: number; children: React.ReactNode }> = ({ open, onClose, width, children }) => {
  useEscapeKey(onClose, open);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 whitespace-normal text-left" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="max-w-[95vw] max-h-[85vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden" style={{ width }}>
        {children}
      </div>
    </div>
  );
};

/** 「사용법 알려줘」 — 어시스턴트에게 보내는 명령어(도움말 흐름이 알아본다). 언어별 문서에서 온다 */
const usageAsk = (topic: string) => helpContent().triggers.usageOf.replace('{{topic}}', topic);

const HelpMenu: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [openTopic, setOpenTopic] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const assistant = useAssistantStatus();
  const assistantOn = assistant.enabled;
  const dispatch = useDispatch<AppDispatch>();
  // 샘플 교체는 저장 API(서버가 복제)에서만 된다 — 예전 경로(브라우저가 QBase 직접)에서는 감춘다
  const [canRefreshSample, setCanRefreshSample] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => { if (open) usesStoreApi().then(setCanRefreshSample).catch(() => setCanRefreshSample(false)); }, [open]);

  /** 샘플 프로젝트 최신으로 — 내 샘플은 「이름 (이전 날짜)」로 보관하고 최신 샘플을 새로 받는다 */
  const onRefreshSample = async () => {
    setOpen(false);
    if (!(await showConfirm(t('layout:help.sampleRefreshConfirm'), { title: t('layout:help.sampleRefresh'), okText: t('layout:help.sampleRefreshOk') }))) return;
    setRefreshing(true);
    try {
      const r = await refreshSample(t('layout:help.sampleBackupName', { date: formatDateTime(new Date(), { seconds: false }).slice(0, 10) }));
      logUsage('sample.refresh');
      await dispatch(loadProjects());
      dispatch(setSelectedProject(r.project.id));
      showAlert(r.backups.length
        ? t('layout:help.sampleRefreshedKept', { name: r.backups[0].name })
        : t('layout:help.sampleRefreshed'), { title: t('layout:help.sampleRefresh') });
    } catch (e: any) {
      showAlert(t('layout:help.sampleRefreshFailed', { message: e?.message || String(e) }));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // i18n: ask('도움말') · `… 사용법 알려줘` 는 화면 문구가 아니라 어시스턴트에 보내는 질문이다. 도움말 흐름(help/helpWorkflow)이
  // 한국어 문구로 주제를 알아보므로 번역하지 않는다 — 도움말 전환(4단계) 때 함께 바꾼다.
  const ask = (text: string, send = false) => {
    setOpen(false);
    setGuideOpen(false);
    logUsage('assistant.ask', { detail: { from: 'help' } });
    askAssistant(text, { send });
  };

  const item = 'w-full flex items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-100';

  return (
    <>
      <div className="relative" ref={ref}>
        <button type="button" onClick={() => setOpen((v) => !v)} data-point="header:help"
          className="flex items-center gap-1 px-2 py-1.5 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
          title={t('layout:help.menu')} aria-label={t('layout:help.menu')}>
          {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
          <FiHelpCircle size={16} />
          <span className="text-sm">{t('layout:help.menu')}</span>
        </button>
        {open && (
          <div className="absolute right-0 top-[calc(100%+8px)] min-w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg z-[1000] overflow-hidden text-sm">
            <button type="button" className={item} onClick={() => { setOpen(false); logUsage('help.open'); setGuideOpen(true); }}>
              {/* @ts-ignore */}
              <FiBookOpen /> {t('layout:help.guide')}
            </button>
            {assistantOn && (
              <button type="button" className={item} onClick={() => ask(helpContent().triggers.help, true)} title={t('layout:help.askAiTitle')}>
                {/* @ts-ignore */}
                <FiMessageSquare /> {t('layout:help.askAi')}
              </button>
            )}
            {canRefreshSample && (
              <button type="button" className={`${item} border-t border-gray-200 disabled:opacity-50`} disabled={refreshing}
                onClick={onRefreshSample} title={t('layout:help.sampleRefreshTitle')}>
                {/* @ts-ignore */}
                <FiRefreshCw /> {t('layout:help.sampleRefresh')}
              </button>
            )}
            <button type="button" className={`${item} border-t border-gray-200`}
              onClick={() => { setOpen(false); setLogOpen(true); }}
              title={t('layout:help.logTitle')}>
              {/* @ts-ignore */}
              <FiFileText /> {t('layout:help.log')}
            </button>
            <button type="button" className={`${item} border-t border-gray-200`} onClick={() => { setOpen(false); setInfoOpen(true); }}>
              {/* @ts-ignore */}
              <FiInfo /> {t('layout:help.about')}
            </button>
          </div>
        )}
      </div>

      <LogViewerDialog open={logOpen} onClose={() => setLogOpen(false)} />

      {/* 사용 안내 */}
      <Modal open={guideOpen} onClose={() => setGuideOpen(false)} width={680}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="font-semibold text-gray-900 flex items-center gap-2">
            {/* @ts-ignore */}
            <FiBookOpen className="text-blue-500" /> {t('layout:help.guide')}
          </div>
          <button type="button" onClick={() => setGuideOpen(false)} className="text-gray-400 hover:text-gray-700" aria-label={t('layout:help.close')}>
            {/* @ts-ignore */}
            <FiX />
          </button>
        </div>
        <div className="px-4 pt-3 text-xs text-gray-500">
          {t('layout:help.guideIntro')}
          {assistantOn ? t('layout:help.guideIntroAi') : t('layout:help.guideIntroDocs')}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-1.5">
          {usageTopics().map((topic) => {
            const on = openTopic === topic.id;
            return (
              <div key={topic.id} className="rounded-lg border border-gray-200">
                <button type="button" onClick={() => setOpenTopic(on ? null : topic.id)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg">
                  <span className="block text-sm font-medium text-gray-900">{topic.title}</span>
                  <span className="block text-xs text-gray-600 mt-0.5">{topic.summary}</span>
                </button>
                {on && (
                  <div className="px-3 pb-3">
                    <ol className="text-xs text-gray-700 space-y-1 list-decimal pl-4">
                      {topic.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                    {assistantOn && (
                      <div className="mt-2 flex justify-end gap-2">
                        {topic.sampleWorkflow && (
                          <button type="button" onClick={() => ask(usageAsk(topic.title), true)}
                            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                            title={t('layout:help.sampleTitle', { sample: topic.sampleWorkflow })}>
                            {t('layout:help.learnWithSample')}
                          </button>
                        )}
                        <button type="button" onClick={() => ask(usageAsk(topic.title))}
                          className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
                          title={t('layout:help.askTitle')}>
                          {t('layout:help.askAi')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end items-center gap-2 px-4 py-2.5 border-t border-gray-200 bg-gray-50 shrink-0">
          {assistantOn && (
            <button type="button" onClick={() => ask(helpContent().triggers.help, true)}
              className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">
              {t('layout:help.askAi')}
            </button>
          )}
          <button type="button" onClick={() => setGuideOpen(false)} className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">{t('layout:help.close')}</button>
        </div>
      </Modal>

      {/* 정보 */}
      <Modal open={infoOpen} onClose={() => setInfoOpen(false)} width={420}>
        <div className="p-4">
          <div className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
            {/* @ts-ignore */}
            <FiInfo className="text-blue-500" /> OpenFlow
          </div>
          <dl className="text-sm grid grid-cols-[6rem_1fr] gap-y-1.5 text-gray-800">
            <dt className="text-gray-500">{t('layout:help.version')}</dt><dd>v{process.env.REACT_APP_VERSION || '1.0'} beta</dd>
            <dt className="text-gray-500">{t('layout:help.buildTime')}</dt><dd className="tabular-nums">{buildTimeText()}{!IS_DEV_SERVER && BUILD_RELEASE ? <span className="ml-1.5 text-gray-400 font-mono text-xs">{BUILD_RELEASE}</span> : null}</dd>
            <dt className="text-gray-500">{t('layout:help.edition')}</dt><dd>{IS_LOCAL ? t('layout:help.editionLocal') : t('layout:help.editionQbase')}</dd>
            <dt className="text-gray-500">{t('layout:help.engine')}</dt><dd>{IS_LOCAL ? t('layout:help.engineLocal') : 'Apache Airflow'}</dd>
            <dt className="text-gray-500">{t('layout:help.assistant')}</dt>
            <dd>{assistant.enabled ? t('layout:help.assistantOn') : t(`layout:help.assistantOff.${assistant.reason || 'off'}`)}</dd>
          </dl>
          <p className="text-xs text-gray-500 mt-3">
            {t('layout:help.aboutBody')}
            {assistantOn ? t('layout:help.aboutAi') : ''}
          </p>
          <div className="flex justify-end mt-4">
            <button type="button" onClick={() => setInfoOpen(false)} className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">{t('layout:help.close')}</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default HelpMenu;
